import { useMutation, useQueryClient } from "@tanstack/react-query";
import { leagueApi } from "@bw/shared/api";
import type { LeagueRegistrationResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The player SELF-register-into-a-division mutation for the discovery browse (S3b)
 * — POST /leagues/divisions/{division_id}/registrations → LeagueRegistrationResponse
 * via leagueApi.registerForDivision, keyed ONLY by the division_id (the backend
 * resolves the player from the token; NO player_id is sent, so a player can never
 * register someone else). The backend owns ALL authorization, eligibility, capacity
 * and lifecycle logic — the hook owns NONE of it; a failure REJECTS with an
 * ApiError whose `status` the consuming page maps to honest copy (409 full/already-
 * registered, 403|400 ineligible/not-open, 404 not-found).
 *
 * On SUCCESS it invalidates:
 *   - `queryKeys.me.leagueRegistrations()` — the cross-facility "My leagues" arm —
 *     so the newly-joined division appears in S1 (My Leagues) as the server's truth,
 *     never an optimistic local insert.
 *   - the `queryKeys.leagues.all` prefix — so the browsed division's
 *     `registration_count` (and any status the server flips, e.g. → full) re-reads
 *     from the server, never a client-side count bump.
 * A FAILED register invalidates NOTHING (no fabricated membership / count change).
 *
 * The arg is the division_id string. The Register affordance on the division row
 * owns the single-fire guard + the in-flight disabled state (specced by the
 * test-engineer, finalized in GREEN).
 *
 * SCAFFOLD: the mutation seam + the success-invalidation are real; the exact
 * affordance UX + the status→copy error mapping (see mapRegisterError) are wired in
 * the GREEN step.
 */
export function useRegisterForDivision() {
  const queryClient = useQueryClient();
  return useMutation<LeagueRegistrationResponse, Error, string>({
    mutationFn: (divisionId: string) =>
      leagueApi.registerForDivision(divisionId),
    onSuccess: async () => {
      // The newly-joined division must surface in the cross-facility "My leagues"
      // history (S1) — server truth, never an optimistic local insert.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.me.leagueRegistrations(),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.leagues.all }),
      ]);
      // Re-read the browsed divisions so the bumped registration_count (and any
      // server status flip) reflects truth — never a client-side count bump.
    },
  });
}
